import { t } from '@/shared/i18n';

const DAY = 24 * 60 * 60 * 1000;

/** Сегодня в формате YYYY-MM-DD по локальному времени. */
export function todayISO(): string {
  const d = new Date();

  return `${String(d.getFullYear())}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function parseISO(value: string): Date {
  const [y, m, d] = value.split('-').map(Number);

  return new Date(y ?? 1970, (m ?? 1) - 1, d ?? 1);
}

/** Дата коротко: «12 сен», чужой год — «12 сен 2025». */
export function describeDate(value: string): string {
  const date = parseISO(value);
  const sameYear = date.getFullYear() === new Date().getFullYear();

  return date.toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'short',
    ...(sameYear ? {} : { year: 'numeric' }),
  });
}

/**
 * Дедлайн словами: «сегодня», «завтра», «12 сен», «12 сен 2025» — и
 * признак просрочки. Год показывается только чужой: свой и так понятен.
 */
export function describeDue(value: string): { text: string; overdue: boolean; soon: boolean } {
  const date = parseISO(value);
  const now = parseISO(todayISO());
  const diff = Math.round((date.getTime() - now.getTime()) / DAY);

  if (diff === 0) {
    return { text: t.task.today, overdue: false, soon: true };
  }

  if (diff === 1) {
    return { text: t.task.tomorrow, overdue: false, soon: true };
  }

  const sameYear = date.getFullYear() === now.getFullYear();
  const text = date.toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'short',
    ...(sameYear ? {} : { year: 'numeric' }),
  });

  return { text, overdue: diff < 0, soon: diff > 1 && diff <= 3 };
}

/**
 * Время события словами: «только что», «5 мин назад», «2 ч назад»,
 * «вчера, 14:02», дальше — дата. Для комментариев и журнала.
 */
export function describeWhen(iso: string): string {
  const date = new Date(iso);
  const diff = Date.now() - date.getTime();
  const minutes = Math.round(diff / 60000);

  if (minutes < 1) {
    return t.time.justNow;
  }

  if (minutes < 60) {
    return t.time.minutesAgo(minutes);
  }

  const hours = Math.round(minutes / 60);

  if (hours < 24 && date.getDate() === new Date().getDate()) {
    return t.time.hoursAgo(hours);
  }

  const time = date.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
  const yesterday = new Date();

  yesterday.setDate(yesterday.getDate() - 1);

  if (date.toDateString() === yesterday.toDateString()) {
    return `${t.time.yesterday}, ${time}`;
  }

  const sameYear = date.getFullYear() === new Date().getFullYear();

  return `${date.toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'short',
    ...(sameYear ? {} : { year: 'numeric' }),
  })}, ${time}`;
}
